import { useContext, useCallback, useRef, useState, type RefObject } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import GoogleLogo from '@/UI/Icons/GoogleLogo';
import MicrosoftLogo from '@/UI/Icons/MicrosoftLogo';
import { AuthContext } from '@/contexts/AuthContext';
import { color } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import { FigmaLogo } from '@/UI/Icons/FigmaLogo';
import { ACTIVATION_QUERY_PARAMS_KEY } from '@/pages/TeamInviteActivationPage';
import { EXTERNAL_LINKS } from '@/consts/externalLinks.consts';
import { Box } from '@/UI/Box/Box';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { Link } from '@/UI/Link/Link';
import { Typography } from '@/UI/AppTypography/AppTypography';
import {
  AuthenticationTurnstileWidget,
  AuthenticationTurnstileStatus,
  type AuthenticationTurnstileWidgetHandle,
  isTurnstileBlockingSignIn,
} from '@/components/Authentication/AuthenticationTurnstileWidget';
import { TurnstileWidgetTheme } from '@/components/Authentication/TurnstileWidget';
import type { SignInOptions } from '@/contexts/AuthContext';

const signInButtonSx = {
  borderRadius: '8px',
  width: '240px',
  border: `1px solid rgba(242, 240, 248, 0.5)`,
  height: '42px',
  gap: '8px',

  '&:hover': {
    backgroundColor: token.bgSelected,
  },
};

const FirebaseSignInButtons = ({
  signIn,
  from,
  promotion,
  teamInviteToken,
  hintEmail,
  microsoftTenantId,
  initiateRedirectSignIn,
  turnstileRef,
  isChallengeBlocking,
}: {
  signIn: (options: SignInOptions) => Promise<void>;
  from: string;
  promotion: string | null;
  teamInviteToken?: string | null;
  hintEmail?: string | null;
  microsoftTenantId?: string;
  initiateRedirectSignIn: (provider: string, redirectPath: string) => Promise<void>;
  turnstileRef: RefObject<AuthenticationTurnstileWidgetHandle | null>;
  isChallengeBlocking: boolean;
}) => {
  const { t } = useTranslation();

  const googleSignIn = () =>
    void signIn({
      route: from,
      providerType: 'google',
      teamInviteToken,
      hintEmail,
      promotion,
      turnstileToken: turnstileRef.current?.getToken(),
      onSignInFlowFailed: () => turnstileRef.current?.reset(),
    });
  const microsoftSignIn = () =>
    void signIn({
      route: from,
      providerType: 'microsoft',
      teamInviteToken,
      hintEmail,
      microsoftTenantId,
      promotion,
      turnstileToken: turnstileRef.current?.getToken(),
      onSignInFlowFailed: () => turnstileRef.current?.reset(),
    });
  const figmaSignIn = useCallback(() => {
    const currentSearch = window.location.search;
    if (currentSearch) {
      localStorage.setItem(ACTIVATION_QUERY_PARAMS_KEY, currentSearch);
    }
    void initiateRedirectSignIn('figma', window.location.pathname);
  }, [initiateRedirectSignIn]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography
        variant="body-sm-rg"
        color={token.text}
        sx={{ mb: 3, textAlign: 'center', maxWidth: '234px', fontSize: '10px' }}
      >
        {t(I18N_KEYS.SIGN_IN_PAGE.TERMS_TEXT)}{' '}
        <Link
          href={EXTERNAL_LINKS.termsOfService}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: color.Yambo_Blue, textDecoration: 'underline' }}
        >
          {t(I18N_KEYS.SIGN_IN_PAGE.TERMS_OF_SERVICE_LINK)}
        </Link>{' '}
        and{' '}
        <Link
          href={EXTERNAL_LINKS.privacyPolicy}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: color.Yambo_Blue, textDecoration: 'underline' }}
        >
          {t(I18N_KEYS.SIGN_IN_PAGE.PRIVACY_POLICY_LINK)}
        </Link>
        .
      </Typography>
      <ButtonContained
        mode="outlined"
        size="large"
        sx={signInButtonSx}
        onClick={googleSignIn}
        disabled={isChallengeBlocking}
      >
        <GoogleLogo width={24} height={24} title="Google logo" />
        <Typography variant="body-std-rg" color={token.text}>
          {t(I18N_KEYS.SIGN_IN_PAGE.GOOGLE_SIGN_IN_BUTTON)}
        </Typography>
      </ButtonContained>
      <ButtonContained
        mode="outlined"
        size="large"
        sx={signInButtonSx}
        onClick={microsoftSignIn}
        disabled={isChallengeBlocking}
      >
        <MicrosoftLogo width={24} height={24} title="Microsoft logo" />
        <Typography variant="body-std-rg" color={token.text}>
          {t(I18N_KEYS.SIGN_IN_PAGE.MICROSOFT_SIGN_IN_BUTTON)}
        </Typography>
      </ButtonContained>
      <ButtonContained
        mode="outlined"
        size="large"
        sx={signInButtonSx}
        onClick={figmaSignIn}
        disabled={isChallengeBlocking}
      >
        <FigmaLogo width={24} height={24} title="Figma logo" />
        <Typography variant="body-std-rg" color={token.text}>
          {t(I18N_KEYS.SIGN_IN_PAGE.FIGMA_SIGN_IN_BUTTON)}
        </Typography>
      </ButtonContained>
    </Box>
  );
};

// pass token and email and remove form url when enter this one
export function TeamInviteSignin({
  fullScreen = true,
  teamInviteToken,
  hintEmail,
  microsoftTenantId,
}: {
  fullScreen?: boolean;
  teamInviteToken?: string | null;
  hintEmail?: string | null;
  microsoftTenantId?: string;
}) {
  const [searchParams] = useSearchParams();
  const promotion = searchParams.get('promotion');

  const from = '/';
  const { signIn, initiateRedirectSignIn } = useContext(AuthContext);
  const [turnstileStatus, setTurnstileStatus] = useState<AuthenticationTurnstileStatus>(
    AuthenticationTurnstileStatus.Pending,
  );
  const turnstileRef = useRef<AuthenticationTurnstileWidgetHandle>(null);
  const isSignInBlockedByTurnstile = isTurnstileBlockingSignIn(turnstileStatus);

  return (
    <Box
      sx={{
        width: fullScreen ? '100%' : 'auto',
        height: fullScreen ? '100%' : 'auto',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <AuthenticationTurnstileWidget
        ref={turnstileRef}
        onStatusChange={setTurnstileStatus}
        theme={TurnstileWidgetTheme.Dark}
      />
      <FirebaseSignInButtons
        signIn={signIn}
        from={from}
        promotion={promotion}
        teamInviteToken={teamInviteToken}
        hintEmail={hintEmail}
        microsoftTenantId={microsoftTenantId}
        initiateRedirectSignIn={initiateRedirectSignIn}
        turnstileRef={turnstileRef}
        isChallengeBlocking={isSignInBlockedByTurnstile}
      />
    </Box>
  );
}
